import {AxiosResponse} from 'axios';
import {Device} from '../backend/interfaces/device';
import {HealthCheck} from '../backend/interfaces/health-check';
import {PostDevice} from '../backend/requests/post-device';
import {PutDevice} from '../backend/requests/put-device';
import {BackendApiManager} from '../managers/backend-api';
import {Logger} from '../utils/managers/logger';
import {DeviceType} from '../backend/enums/device-type';
import {DeviceWorkgroup} from '../backend/enums/device-workgroup';

export class DevicesRepository {
  // Singleton Instance
  private static _instance: DevicesRepository;

  // Private
  private logger: Logger;

  static get instance(): DevicesRepository {
    if (!this._instance) {
      this._instance = new DevicesRepository();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('DevicesRepository');
  }

  // Get all devices in an entity as professional caregiver
  public async getInEntityAsAffiliatedForEntity(
    entityId: string,
    limit: number,
    skip: number,
    onlyNotAssignedToUser: boolean,
    serialNumberSearch?: string,
    workgroup?: DeviceWorkgroup
  ): Promise<{totalCount: number; data: Array<Device>}> {
    // Set url
    let url: string = `/entities/${entityId}/devices?skip=${skip}&limit=${limit}&onlyNotAssignedToUser=${onlyNotAssignedToUser}`;

    // Check search
    if (serialNumberSearch && serialNumberSearch.trim().length !== 0) {
      // Add to the url
      url += `&serialNumber=${serialNumberSearch}`;
    }

    // Check workgroup
    if (workgroup) {
      url += `&workgroups=${workgroup}`;
    }

    // Get devices from backend
    const response: AxiosResponse<Array<Device>> =
      await BackendApiManager.instance.get<Array<Device>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get all devices of a user as professional caregiver
  public async getForUserAsAffiliatedForEntity(
    userId: string,
    limit: number,
    skip: number,
    entityId: string
  ): Promise<{totalCount: number; data: Array<Device>}> {
    // Set url
    let url: string = `/users/${userId}/devices?skip=${skip}&limit=${limit}&entityId=${entityId}`;

    // Get devices from backend
    const response: AxiosResponse<Array<Device>> =
      await BackendApiManager.instance.get<Array<Device>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get all devices as system admin
  public async getAsSystemAdministrator(
    limit: number,
    skip: number,
    entityId?: string,
    userId?: string,
    onlyNotAssignedToUser?: boolean,
    serialNumberSearch?: string
  ): Promise<{totalCount: number; data: Array<Device>}> {
    // Set url
    let url: string = `/devices?skip=${skip}&limit=${limit}`;

    // Serial number search
    if (serialNumberSearch && serialNumberSearch.trim().length !== 0) {
      // Add to the url
      url += `&serialNumber=${serialNumberSearch}`;
    }

    // Entity id
    if (entityId && entityId.trim().length !== 0) {
      // Add to the url
      url += `&entityId=${entityId}`;
    }

    // User id
    if (userId && userId.trim().length !== 0) {
      // Add to the url
      url += `&userId=${userId}`;
    }

    // Only not assigned to user
    if (typeof onlyNotAssignedToUser === 'boolean') {
      // Add to the url
      url += `&onlyNotAssignedToUser=${onlyNotAssignedToUser}`;
    }

    // Get devices from backend
    const response: AxiosResponse<Array<Device>> =
      await BackendApiManager.instance.get<Array<Device>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Update a device by id
  public async updateById(id: string, body: PutDevice): Promise<Device> {
    // Update device
    const response: AxiosResponse<Device> = await BackendApiManager.instance.put<Device>(
      `/devices/${id}`,
      body
    );

    // Return
    return response.data;
  }

  // Get device by id
  public async getById(id: string): Promise<Device> {
    // Get device
    const response: AxiosResponse<Device> = await BackendApiManager.instance.get<Device>(
      `/devices/${id}`
    );

    // Return
    return response.data;
  }

  // Delete device by id
  public async deleteById(id: string): Promise<void> {
    // Delete device
    await BackendApiManager.instance.delete<void>(`/devices/${id}`);
  }

  // Create device
  public async create(body: PostDevice): Promise<Device> {
    // Create device
    const response: AxiosResponse<Device> = await BackendApiManager.instance.post<Device>(
      '/devices',
      body
    );

    // Return
    return response.data;
  }

  // Get device health by id
  public async getHealthById(id: string): Promise<HealthCheck> {
    // Get device health
    const response: AxiosResponse<HealthCheck> = await BackendApiManager.instance.get<HealthCheck>(
      `/devices/${id}/health`
    );

    // Return
    return response.data;
  }

  // Create device for entity
  public async createForEntity(
    entityId: string,
    body: {
      serialNumber: string;
      authenticationKey: string;
      type?: DeviceType;
      number?: string;
    }
  ): Promise<Device> {
    // Create device
    const response: AxiosResponse<Device> = await BackendApiManager.instance.post<Device>(
      `/entities/${entityId}/devices`,
      body
    );

    // Return
    return response.data;
  }

  public async removeFromEntity(
    entityId: string,
    deviceId: string,
    body?: {
      authenticationKey: string;
    }
  ): Promise<Device> {
    // Create device
    let response: AxiosResponse<Device>;

    if (body) {
      response = await BackendApiManager.instance.delete<Device>(
        `/entities/${entityId}/devices/${deviceId}`,
        {
          data: body,
        }
      );
    } else {
      response = await BackendApiManager.instance.delete<Device>(
        `/entities/${entityId}/devices/${deviceId}`
      );
    }

    // Return
    return response.data;
  }
}
